/**
 * Custom Hook: useMakeModelLookup
 * Handles 3-tier fallback for Make/Model ID lookup:
 * 1. Spreadsheet (Google Sheets CSV data)
 * 2. Hardcoded constants (CEI_MAKES, NICON_MAKES)
 * 3. API fallback (for models only)
 */

import { useCallback, useState } from "react";
import { showSuccessToast, showErrorToast } from "../utils/toastFactory";
import FixedAgent from "../utils/api/FixedAgent";

export const useMakeModelLookup = (
  makeModelMapping,
  renewData,
  ceiMake,
  niconMake,
  token,
) => {
  const [makeIdLoading, setMakeIdLoading] = useState(false);
  const [makeIdStatus, setMakeIdStatus] = useState(null); // 'success' | 'error' | null
  const [modelIdLoading, setModelIdLoading] = useState(false);
  const [modelIdStatus, setModelIdStatus] = useState(null); // 'success' | 'error' | null

  /**
   * Lookup Make ID with 3-tier fallback
   */
  const lookupMakeId = useCallback(
    async (makeName) => {
      if (!makeName) {
        return null;
      }

      console.log("[Make Lookup] Starting lookup for:", makeName);

      setMakeIdLoading(true);
      setMakeIdStatus(null);

      // Simulate network delay for visual feedback
      await new Promise((resolve) => setTimeout(resolve, 500));

      let foundId = null;
      let foundInSpreadsheet = false;

      // Tier 1: Try spreadsheet lookup first if data is loaded
      if (makeModelMapping.data.length > 0) {
        console.log("[Make Lookup] Trying spreadsheet lookup...");
        const insuranceTypeKey = "Make ID";

        const match = makeModelMapping.data.find(
          (row) =>
            row["Make Name"]?.toLowerCase().trim() ===
            makeName.toLowerCase().trim(),
        );

        if (match && match[insuranceTypeKey]) {
          foundId = match[insuranceTypeKey];
          foundInSpreadsheet = true;
          console.log("[Make Lookup] ✅ Found in spreadsheet:", {
            makeName,
            id: foundId,
            column: insuranceTypeKey,
          });
        } else {
          console.log("[Make Lookup] ❌ Not found in spreadsheet:", {
            match: match ? "Found row but missing column" : "No matching row",
            insuranceTypeKey,
            availableColumns: match ? Object.keys(match) : [],
            sampleRow: match,
          });
        }
      } else {
        console.log(
          "[Make Lookup] Spreadsheet data not loaded, going to fallback",
        );
      }

      // Tier 2: Fallback to hardcoded arrays if not found in spreadsheet
      if (!foundId) {
        console.log("[Make Lookup] Trying hardcoded fallback...");
        const makeArray =
          renewData.insuranceType === "NICON" ? niconMake : ceiMake;
        const hardcodedMatch = makeArray.find(
          (make) =>
            make.name?.toLowerCase().trim() === makeName.toLowerCase().trim(),
        );

        if (hardcodedMatch) {
          foundId = hardcodedMatch.id;
          console.log("[Make Lookup] ✅ Found in fallback:", {
            makeName,
            id: foundId,
          });
        } else {
          console.log("[Make Lookup] ❌ Not found in fallback either");
        }
      }

      setMakeIdLoading(false);
      console.log("[Make Lookup] Final result:", {
        foundId,
        source: foundInSpreadsheet
          ? "spreadsheet"
          : foundId
            ? "fallback"
            : "not found",
      });

      if (foundId) {
        setMakeIdStatus("success");
        setTimeout(() => setMakeIdStatus(null), 2000);
        showSuccessToast(
          foundInSpreadsheet
            ? "Make ID found"
            : "Make ID found (using fallback data)",
        );
        return foundId;
      } else {
        setMakeIdStatus("error");
        setTimeout(() => setMakeIdStatus(null), 2000);
        showErrorToast("Make ID not found in database");
        return null;
      }
    },
    [makeModelMapping.data, renewData.insuranceType, niconMake, ceiMake],
  );

  /**
   * Lookup Model ID with 3-tier fallback
   */
  const lookupModelId = useCallback(
    async (modelName, makeName) => {
      if (!modelName || !makeName) {
        return null;
      }

      setModelIdLoading(true);
      setModelIdStatus(null);

      // Simulate network delay for visual feedback
      await new Promise((resolve) => setTimeout(resolve, 500));

      let foundId = null;
      let foundInSpreadsheet = false;

      // Tier 1: Try spreadsheet lookup first if data is loaded
      if (makeModelMapping.data.length > 0) {
        const insuranceTypeKey = "Model ID";

        const match = makeModelMapping.data.find(
          (row) =>
            row["Make Name"]?.toLowerCase().trim() ===
              makeName.toLowerCase().trim() &&
            row["Model Name"]?.toLowerCase().trim() ===
              modelName.toLowerCase().trim(),
        );

        if (match && match[insuranceTypeKey]) {
          foundId = match[insuranceTypeKey];
          foundInSpreadsheet = true;
        }
      }

      // Tier 2 & 3: API fallback for models (CEI/NICON)
      if (!foundId && makeName) {
        console.log(
          "[Model Lookup] Spreadsheet lookup failed, trying API fallback...",
        );
        try {
          const makeArray =
            renewData.insuranceType === "NICON" ? niconMake : ceiMake;
          const makeObj = makeArray.find(
            (m) =>
              m.name?.toLowerCase().trim() === makeName.toLowerCase().trim(),
          );

          let makeId = makeObj?.id;

          if (makeId) {
            const apiRes = await (renewData.insuranceType === "NICON"
              ? FixedAgent.getNiconVehicleModels({ id: makeId, token })
              : FixedAgent.getVehicleModelsByID({ id: makeId, token }));

            const models =
              renewData.insuranceType === "NICON"
                ? apiRes.data.data.model
                : apiRes.data.modelToReturn;

            if (Array.isArray(models)) {
              const foundModel = models.find((m) => {
                const mName =
                  renewData.insuranceType === "NICON" ? m.name : m.modelName;
                return (
                  mName?.toLowerCase().trim() === modelName.toLowerCase().trim()
                );
              });

              if (foundModel) {
                foundId =
                  renewData.insuranceType === "NICON"
                    ? foundModel.id
                    : foundModel.vehicleModelId;
                console.log("[Model Lookup] ✅ Found in API fallback:", {
                  modelName,
                  id: foundId,
                });
              } else {
                console.log("[Model Lookup] ❌ Not found in API response");
              }
            }
          }
        } catch (err) {
          console.error("[Model Lookup] API fallback failed:", err);
        }
      }

      setModelIdLoading(false);

      if (foundId) {
        setModelIdStatus("success");
        setTimeout(() => setModelIdStatus(null), 2000);
        showSuccessToast(
          foundInSpreadsheet
            ? "Model ID found in database"
            : "Model ID found (using API fallback)",
        );
        return foundId;
      } else {
        setModelIdStatus("error");
        setTimeout(() => setModelIdStatus(null), 2000);
        showErrorToast("Model ID not found in database");
        return null;
      }
    },
    [makeModelMapping.data, renewData.insuranceType, niconMake, ceiMake, token],
  );

  return {
    lookupMakeId,
    lookupModelId,
    makeIdLoading,
    makeIdStatus,
    modelIdLoading,
    modelIdStatus,
  };
};
